import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";

import { DialogBox, DialogBoxFooterType } from "~/core";
import { withUser } from "~/hocs";
import { mapToolsActions, mapToolsSelectors } from "~/map";
import { getSystemAreaLimit } from "~/login";
import { GeometryUtils, ToolConfig } from "@ai360/core";

import { getFieldInfoModel, getAgvanceFieldList } from "../../../selectors";
import { fetchFieldInformation, updateFieldInfo, saveFieldInformation } from "../../../actions";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import { getTheUserPersonalityId } from "~/login/selectors";
import { getCropData, isCropUnlinked } from "~/admin/setup/customer/use-crop-list";
import { FieldInformationTitleBar } from "../components/field-information-title-bar/field-information-title-bar";
import { FieldInformationMenu } from "../components/field-information-menu/field-information-menu";
import FieldInformationDetail from "../components/field-information-detail/field-information-detail";
import { FieldInformationButtonBar } from "../components/field-information-button-bar/field-information-button-bar";

import "./field-information.css";
import { messages } from "./i18n-messages";
import { setFieldsToInvalidate } from "~/map/components/map-control/actions";
import { FieldResurfacePrompt } from "../../../../common";

import { v4 as uuid } from "uuid";
import FieldBoundaryOverlap from "../components/field-boundary-overlap/field-boundary-overlap";
import PortalBoundaryUpdate from "../components/portal-boundary-update/portal-boundary-update";
import AddBoundaryToPortal from "../components/add-boundary-to-portal/add-boundary-to-portal";
import PortalInfoMessageDialog from "../components/add-boundary-to-portal/portal-info-message-dialog";

class FieldInformation extends React.Component {
    static propTypes = {
        activeCustomer: PropTypes.object,
        activeField: PropTypes.object,
        agvanceFieldList: PropTypes.array,
        cropData: PropTypes.array,
        editFieldGuid: PropTypes.string,
        fetchFieldInformation: PropTypes.func,
        fieldEditGeometries: PropTypes.array,
        fieldEditGeometriesChanged: PropTypes.bool,
        intl: intlShape.isRequired,
        invalidateFieldGuids: PropTypes.func.isRequired,
        onCloseFieldInformation: PropTypes.func.isRequired,
        onSetToolsetDisabled: PropTypes.func.isRequired,
        personalityId: PropTypes.number,
        maximumArea: PropTypes.number,
        saveFieldInformation: PropTypes.func.isRequired,
        updateFieldModel: PropTypes.func,
        userInfo: PropTypes.object,
        userRole: PropTypes.object,
    };
    static defaultProps = {};

    constructor(props) {
        super(props);
        this.state = {
            isConfirmationModalOpen: false,
            isInvalidBillingSplits: false,
            isInvalidGeometries: false,
            isMaxAreaModalOpen: false,
            yesAction: null,
            showResurfacePrompt: false,
            insuranceCropUnlinked: false,
        };
    }

    _onInsuranceCropUnlinkedChange = (hasUnlinkedCrop) => {
        this.setState({ insuranceCropUnlinked: hasUnlinkedCrop });
    };

    async componentDidMount() {
        this.props.fetchFieldInformation(this.props.editFieldGuid);
    }

    _closeConfirmDialog() {
        this.setState({
            isConfirmationModalOpen: false,
            yesAction: null,
        });
    }

    _closeInvalidBillingSplits() {
        this.setState({
            isInvalidBillingSplits: false,
        });
    }

    _closeInvalidGeometries() {
        this.setState({
            isInvalidGeometries: false,
        });
    }

    _closeMaxAreaDialog() {
        this.setState({
            isMaxAreaModalOpen: false,
        });
    }

    _confirmYes() {
        if (this.state.yesAction) {
            this.state.yesAction();
        }
        this._closeConfirmDialog();
    }

    _getCustomerName = (customer) => {
        return customer ? customer.name : "";
    };
    _getFieldName = (field) => {
        return field ? field.fieldName : "";
    };
    _getFarmName = (field) => {
        return field ? field.farmName : "";
    };

    _saveFieldInformation(fieldToSave) {
        this.props.onSetToolsetDisabled(true);
        this.props.saveFieldInformation(fieldToSave, this.props.onCloseFieldInformation);
        if (this.props.invalidateFieldGuids) {
            this.props.invalidateFieldGuids(fieldToSave.fieldGuid);
        }
    }

    _showConfirmDialog(yesAction) {
        this.setState({
            isConfirmationModalOpen: true,
            yesAction,
        });
    }

    _showInvalidBillingSplitsDialog() {
        this.setState({
            isInvalidBillingSplits: true,
        });
    }

    _showInvalidGeometriesDialog() {
        this.setState({
            isInvalidGeometries: true,
        });
    }

    _showMaxAreaDialog() {
        this.setState({
            isMaxAreaModalOpen: true,
        });
    }

    _showResurfacePrompt(fieldToSave) {
        this.setState({ showResurfacePrompt: true, fieldToSave });
    }
    _onResurfacePromptClosed = (resurfaceEventResult) => {
        const fieldToSave = this.state.fieldToSave;
        fieldToSave.resurfaceEventResult = resurfaceEventResult;

        this.setState({ showResurfacePrompt: false }, () =>
            this._saveFieldInformation(fieldToSave)
        );
    };

    _onResurfacePromptCanceled = () => {
        this.setState({ showResurfacePrompt: false, fieldToSave: null });
    };

    _validFieldInformation = (fieldToValidate, fieldGeometries, userRole = null) => {
        if (fieldToValidate.fieldName === "") {
            //console.error("Field Name cannot be blank");
            return false; //TODO: actually handle it
        } else if (
            AgvanceUtils.isAgvanceConnected(this.props.personalityId) &&
            userRole?.agvanceLocation &&
            fieldToValidate?.agvanceLocationId == null
        ) {
            return false;
        } else if (fieldGeometries && fieldGeometries.length === 0) {
            //console.error("Must have a field boundary");
            return false; //TODO: actually handle it
        } else if (isCropUnlinked(fieldToValidate.cropGuid, this.props.cropData)) {
            return false;
        } else if (this.state.insuranceCropUnlinked) {
            return false;
        }

        return true;
    };

    _validateBillingSplits = (fieldToValidate) => {
        if (!fieldToValidate.billingSplits || fieldToValidate.billingSplits.length === 0) {
            return true;
        }
        const keysToCheck = ["split1Percent", "split2Percent", "split3Percent"];
        const filteredBillingSplits = fieldToValidate.billingSplits.filter(
            (split) => split.splitCustomerID && split.splitCustomerID.trim() !== ""
        );
        const sums = filteredBillingSplits.reduce((accumulator, currentObject) => {
            keysToCheck.forEach((key) => {
                const sum = (accumulator[key] || 0) + currentObject[key];
                accumulator[key] = Math.round(sum * 10000) / 10000;
            });
            return accumulator;
        }, {});
        fieldToValidate.billingSplits = filteredBillingSplits;
        const allVals = Object.values(sums);
        return allVals.every((val) => val === 100);
    };

    onSaveFieldInformation = () => {
        const { activeField, fieldEditGeometries, fieldEditGeometriesChanged } = this.props;
        const fieldBoundaryPolygons = [];

        let fieldToSave = { ...activeField };
        const isNewField = fieldToSave.fieldGuid === "" || fieldToSave.fieldGuid == null;
        var newCalculatedArea = 0;
        if ((fieldEditGeometriesChanged || isNewField) && fieldEditGeometries) {
            fieldEditGeometries.forEach((geometry) => {
                fieldBoundaryPolygons.push({
                    fieldBoundaryPolygonGuid: uuid(),
                    importFieldBoundaryGuids: null,
                    fieldGuidsToCombine: null,
                    shapes: [JSON.stringify(geometry.toJSON())],
                });
                newCalculatedArea += GeometryUtils.calculateArea(geometry);
            });
        }

        fieldToSave.fieldBoundary = {
            ...fieldToSave.fieldBoundary,
            fieldGuid: fieldToSave.fieldGuid,
            fieldBoundaryPolygons,
        };

        if (fieldBoundaryPolygons && fieldBoundaryPolygons.length > 0) {
            fieldToSave.fieldBoundary.fieldBoundaryGuid = uuid();
            const fixedCalculatedArea = newCalculatedArea.toFixed(2);
            fieldToSave.acres = fixedCalculatedArea;
            fieldToSave.fieldBoundary.calculateArea = fixedCalculatedArea;
        }

        const filteredAgvanceField = this.props.agvanceFieldList.filter(
            (af) => af.name === fieldToSave.fieldName && af.farmName === fieldToSave.farmName
        );
        const isBillingSplitsValid = this._validateBillingSplits(fieldToSave);

        if (
            filteredAgvanceField.length > 0 &&
            !filteredAgvanceField[0].mappingGuid &&
            filteredAgvanceField[0].agvanceGrowerGuid === fieldToSave.agvanceCustomerGuid
        ) {
            fieldToSave.agvanceFieldGuid = filteredAgvanceField[0].agvanceGuid;
            fieldToSave.agvanceFieldId = filteredAgvanceField[0].fieldId;
            fieldToSave.agvanceFieldOrgLevelGuid = filteredAgvanceField[0].orgLevelGuid;
            fieldToSave.agvanceAutoMapField = true;
        }

        if (this._validFieldInformation(fieldToSave, fieldEditGeometries)) {
            if (GeometryUtils.verifyIntersections(fieldEditGeometries)) {
                this._showInvalidGeometriesDialog();
            } else if (GeometryUtils.checkMaxAcreage(fieldEditGeometries, this.props.maximumArea)) {
                this._showMaxAreaDialog();
            } else if (GeometryUtils.checkMinAcreage(fieldEditGeometries)) {
                this._showConfirmDialog(() => this._showResurfacePrompt(fieldToSave));
            } else if (!isBillingSplitsValid) {
                this._showInvalidBillingSplitsDialog();
            } else {
                this._showResurfacePrompt(fieldToSave);
            }
        }
    };

    getAcresCalculation = () => {
        let fieldEditGeometries = this.props.fieldEditGeometries;
        let totalAcres = 0;
        if (fieldEditGeometries) {
            fieldEditGeometries.forEach((polygon) => {
                totalAcres += GeometryUtils.calculateArea(polygon);
            });
        }
        return totalAcres;
    };

    render() {
        const { activeCustomer, activeField } = this.props;
        const { formatMessage } = this.props.intl;
        const minAcres = ToolConfig.minimumAcreage;
        const maxAcres = this.props.maximumArea;
        const totalAcres = this.getAcresCalculation();

        return (
            <div className="field-information">
                <FieldInformationTitleBar
                    fieldName={this._getFieldName(activeField)}
                    customerName={this._getCustomerName(activeCustomer)}
                    farmName={this._getFarmName(activeField)}
                    acres={totalAcres}
                    lockBoundary={activeField.lockBoundary}
                />
                <div className="field-information-content">
                    <FieldInformationMenu />
                    <FieldInformationDetail
                        activeField={this.props.activeField}
                        customerGuid={activeCustomer.customerGuid}
                        editFieldGuid={this.props.editFieldGuid}
                        hasActiveInactive={this.props.userInfo.role.activeInactive}
                        onInsuranceCropUnlinkedChange={this._onInsuranceCropUnlinkedChange}
                        updateFieldModel={(f) => this.props.updateFieldModel(f)}
                    />
                </div>
                <FieldInformationButtonBar
                    saveIsEnabled={this._validFieldInformation(
                        this.props.activeField,
                        this.props.fieldEditGeometries,
                        this.props.userRole
                    )}
                    onSave={() => this.onSaveFieldInformation()}
                    onCancel={() => this.props.onCloseFieldInformation()}
                />
                <DialogBox
                    title={formatMessage(messages.confirmSaveBelowTitle)}
                    footerType={DialogBoxFooterType.YES_NO}
                    isOpen={this.state.isConfirmationModalOpen}
                    onAction={() => this._confirmYes()}
                    onClose={() => this._closeConfirmDialog()}
                    testId="field-information-min-area-modal"
                >
                    {formatMessage(messages.confirmSaveBelowMinAcreage, {
                        minAcres,
                    })}
                </DialogBox>
                <DialogBox
                    title={formatMessage(messages.errorAboveMaxAcreageTitle)}
                    isOpen={this.state.isMaxAreaModalOpen}
                    onAction={() => this._closeMaxAreaDialog()}
                    onClose={() => this._closeMaxAreaDialog()}
                    testId="field-information-max-area-modal"
                >
                    {formatMessage(messages.errorAboveMaxAcreage, { maxAcres })}
                </DialogBox>
                <DialogBox
                    title={formatMessage(messages.errorInvalidGeometriesTitle)}
                    isOpen={this.state.isInvalidGeometries}
                    onClose={() => this._closeInvalidGeometries()}
                    testId="field-information-invalid-geometries-modal"
                >
                    {formatMessage(messages.errorInvalidGeometries)}
                </DialogBox>
                <DialogBox
                    title={formatMessage(messages.errorInvalidBillSplitsTitle)}
                    isOpen={this.state.isInvalidBillingSplits}
                    onClose={() => this._closeInvalidBillingSplits()}
                    testId="field-information-invalid-bill-splits-modal"
                >
                    {formatMessage(messages.errorInvalidBillSplits)}
                </DialogBox>
                {this.state.editFieldGuid !== "" && (
                    <FieldResurfacePrompt
                        fieldGuid={this.props.editFieldGuid}
                        isOpen={this.state.showResurfacePrompt}
                        onClose={this._onResurfacePromptClosed}
                        onCancel={this._onResurfacePromptCanceled}
                    />
                )}
                <FieldBoundaryOverlap />
                <PortalBoundaryUpdate />
                <AddBoundaryToPortal />
                <PortalInfoMessageDialog />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    activeField: getFieldInfoModel(state),
    cropData: getCropData(state),
    fieldEditGeometries: mapToolsSelectors.getFieldEditGeometries(state),
    fieldEditGeometriesChanged: mapToolsSelectors.getFieldEditsChanged(state),
    maximumArea: getSystemAreaLimit(state),
    agvanceFieldList: getAgvanceFieldList(state),
    personalityId: getTheUserPersonalityId(state),
});
const mapDispatchToProps = (dispatch, ownProps) => ({
    fetchFieldInformation: () =>
        dispatch(
            fetchFieldInformation(ownProps.activeCustomer.customerGuid, ownProps.editFieldGuid)
        ),
    invalidateFieldGuids: (guid) => dispatch(setFieldsToInvalidate(guid)),
    onSetToolsetDisabled: (flag) => dispatch(mapToolsActions.setToolsetDisabled(flag)),
    saveFieldInformation: (f, c) => dispatch(saveFieldInformation(f, c)),
    updateFieldModel: (f) => dispatch(updateFieldInfo(f)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withUser(injectIntl(FieldInformation)));
